import { createFileRoute } from "@tanstack/react-router";
import { alternateLinkList } from "@/lib/i18n/locale";
import { LegalPage, P, UL, H3 } from "@/components/marketing/LegalPage";

export const Route = createFileRoute("/terms")({
  head: () => ({
    meta: [
      { title: "Terms of Service — banana.cards" },
      { name: "description", content: "The terms governing your use of Banana Cards' digital and physical products and services." },
      { property: "og:title", content: "Terms of Service — banana.cards" },
      { property: "og:description", content: "Read the Banana Cards Terms of Service." },
    ],
    links: alternateLinkList("/terms", "en"),
  }),
  component: TermsPage,
});

export function TermsPage() {
  return (
    <LegalPage
      eyebrow="Legal"
      title="Terms of"
      italic="Service."
      updated="January 2026"
      intro="Welcome to Banana Cards. These Terms govern your access to and use of our digital and physical products and services — including websites, apps, NFC cards, digital profiles, CRM, AI tools, and more. By using any part of the Services, you agree to be bound by these Terms."
      related={[
        { label: "Privacy Policy", to: "/privacy" },
        { label: "Legal Guidelines", to: "/legal-guidelines" },
        { label: "Contact us", to: "/contact" },
      ]}
      sections={[
        {
          id: "general",
          title: "General Terms & Applicability",
          body: (
            <>
              <P>These Terms apply to all users, customers, partners, and affiliates interacting with Banana Cards Company Limited and Six Studio Creative FZCO ("Banana Cards", "we", "us", or "our").</P>
              <P>We reserve the right to update or modify these Terms at any time. Updated Terms become effective upon publication. Continued use of the Services after changes take effect constitutes acceptance of the revised Terms.</P>
              <P>If you do not agree to these Terms, you must not use the Services.</P>
            </>
          ),
        },
        {
          id: "services",
          title: "Description of Services",
          body: (
            <>
              <P>Banana Cards provides a digital identity and networking platform that includes:</P>
              <UL items={[
                "Physical NFC business cards",
                "Virtual (digital-only) cards",
                "Hosted digital profiles and mini-sites",
                "Contact and relationship management (CRM)",
                "AI-powered tools and assistants",
                "Contract creation and electronic signatures",
                "Analytics, reporting, and integrations",
                "Optional add-on applications (\"Banana Apps\")",
              ]} />
              <P>Some features are available only under paid subscription plans.</P>
            </>
          ),
        },
        {
          id: "accounts",
          title: "Accounts & Eligibility",
          body: (
            <>
              <P>You must provide accurate, complete, and current information when creating an account. You are responsible for maintaining the confidentiality of your login credentials and all activities under your account.</P>
              <P>You must not:</P>
              <UL items={[
                "Impersonate another person or entity",
                "Misuse the platform for fraudulent or unlawful purposes",
                "Attempt to bypass system controls or attribution mechanisms",
              ]} />
              <P>Banana Cards reserves the right to suspend or terminate accounts that violate these Terms.</P>
            </>
          ),
        },
        {
          id: "products",
          title: "Products, Plans & Pricing",
          body: (
            <>
              <H3>Hardware (NFC Cards)</H3>
              <P>Physical cards are sold separately or bundled depending on your selected plan. Once produced or shipped, hardware purchases are non-refundable unless required by law or covered by a specific warranty.</P>
              <H3>Software Plans</H3>
              <P>Banana Cards offers multiple subscription plans. Features and limits vary by plan.</P>
              <H3>Pricing Changes</H3>
              <P>We may update pricing, features, or plan structures from time to time. Changes apply prospectively and do not retroactively alter already-paid periods unless required by law.</P>
            </>
          ),
        },
        {
          id: "trials",
          title: "Free Trials",
          body: (
            <>
              <P>Banana Cards may offer free trials for software subscription plans only.</P>
              <UL items={[
                "Trials provide temporary access to selected paid features",
                "Trials do not guarantee free or discounted hardware unless explicitly stated",
                "Trials automatically convert to paid subscriptions unless cancelled before the trial ends",
                "No refunds are issued for charges incurred after a trial converts",
              ]} />
              <P>If a trial is not converted to a paid plan, access to paid features may be limited or disabled.</P>
            </>
          ),
        },
        {
          id: "billing",
          title: "Subscriptions, Billing & Renewals",
          body: (
            <UL items={[
              "Paid subscriptions are billed on a recurring basis (monthly or annually, depending on plan)",
              "Subscriptions renew automatically unless cancelled before the renewal date",
              "You are responsible for maintaining valid payment details",
              "Fees are exclusive of applicable taxes unless stated otherwise",
              "Cancellation stops future billing but does not retroactively refund unused time unless required by law",
            ]} />
          ),
        },
        {
          id: "data",
          title: "Data Ownership & Fair Use",
          body: (
            <>
              <H3>Data Ownership</H3>
              <P>You retain ownership of your content, contacts, and data uploaded to the platform. Banana Cards processes data solely to provide the Services.</P>
              <H3>Data Retention</H3>
              <P>Upon cancellation or termination, we may retain your data for a limited period before deletion, subject to legal and operational requirements.</P>
              <H3>Fair Use</H3>
              <UL items={[
                "Automated messaging must comply with anti-spam laws",
                "Excessive or abusive usage may require a plan upgrade or result in suspension",
                "Scraping, manipulation, or misuse of data is prohibited",
              ]} />
            </>
          ),
        },
        {
          id: "hardware",
          title: "Hardware, Shipping & Risk",
          body: (
            <UL items={[
              "Production begins once an order is confirmed",
              "Custom or personalized cards are non-returnable once produced",
              "Risk transfers to the customer upon handover to the carrier",
              "Customers are responsible for customs duties, import taxes, or clearance fees",
              "If delivery fails due to incorrect address details, reshipping fees may apply",
            ]} />
          ),
        },
        {
          id: "ip",
          title: "Intellectual Property",
          body: (
            <>
              <P>All Banana Cards software, branding, designs, and systems are owned by Six Studio Creative FZCO and licensed to Banana Cards Company Ltd. You may not copy, modify, resell, or reverse-engineer any part of the Services without written permission.</P>
              <P>User-generated content remains your property but grants Banana Cards a limited licence to operate and display it within the Services.</P>
            </>
          ),
        },
        {
          id: "termination",
          title: "Account Suspension & Termination",
          body: (
            <>
              <P>We may suspend or terminate access if:</P>
              <UL items={[
                "These Terms are violated",
                "Fraudulent, abusive, or unethical activity is detected",
                "Legal or regulatory requirements demand it",
              ]} />
              <P>Termination does not relieve you of payment obligations already incurred.</P>
            </>
          ),
        },
        {
          id: "warranties",
          title: "Disclaimer of Warranties",
          body: <P>The Services are provided on an "as is" and "as available" basis. To the maximum extent permitted by law, Banana Cards disclaims all warranties, express or implied, including fitness for a particular purpose and uninterrupted availability.</P>,
        },
        {
          id: "liability",
          title: "Limitation of Liability",
          body: (
            <>
              <P>To the fullest extent permitted by law:</P>
              <UL items={[
                "Banana Cards is not liable for indirect, incidental, or consequential damages",
                "Total liability is limited to the amount paid by you for the specific product or subscription giving rise to the claim",
              ]} />
            </>
          ),
        },
        {
          id: "law",
          title: "Governing Law & Contact",
          body: (
            <>
              <P>These Terms are governed by and construed in accordance with the laws of the United Arab Emirates, unless otherwise required by mandatory local law.</P>
              <P>For support, legal notices, or questions about these Terms, contact <a href="mailto:hello@banana.cards" className="font-bold underline-offset-4 hover:underline">hello@banana.cards</a>.</P>
            </>
          ),
        },
      ]}
    />
  );
}